/**
 * Helper functions and badge components for PODetailModal.
 */

import type { PODocument, POChangeLog } from '../../lib/api';
import type { TimelineItem } from './timeline/VerticalTimeline';

export const getStatusBadgeClass = (status: string) => {
  const statusClasses: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    sent: 'bg-blue-100 text-blue-800',
    acknowledged: 'bg-green-100 text-green-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    delivered: 'bg-purple-100 text-purple-800',
    closed: 'bg-slate-100 text-slate-800',
  };
  return statusClasses[status] || 'bg-gray-100 text-gray-800';
};

export const getAcknowledgeStatusBadge = (status?: string) => {
  if (!status) return null;

  const statusConfig: Record<string, { label: string; className: string }> = {
    new: { label: 'New', className: 'bg-blue-200 text-blue-900' },
    declined: { label: 'Declined', className: 'bg-red-200 text-red-900' },
    change_proposed_supplier: { label: 'Change Proposed (S)', className: 'bg-orange-200 text-orange-900' },
    change_proposed_customer: { label: 'Change Proposed (C)', className: 'bg-amber-200 text-amber-900' },
    wip: { label: 'WIP', className: 'bg-indigo-200 text-indigo-900' },
    accepted: { label: 'Accepted', className: 'bg-green-200 text-green-900' },
    despatched: { label: 'Dispatched', className: 'bg-purple-200 text-purple-900' },
    change_declined: { label: 'Change Declined', className: 'bg-red-300 text-red-950' },
    cancelled: { label: 'Cancelled', className: 'bg-slate-200 text-slate-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-200 text-gray-900' };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

export const getLineStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; className: string }> = {
    open: { label: 'Open', className: 'bg-blue-100 text-blue-800' },
    partially_received: { label: 'Partial', className: 'bg-yellow-100 text-yellow-800' },
    received: { label: 'Received', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};


export const formatFieldName = (field: string) => {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatFileSize = (bytes?: number) => {
  if (!bytes) return 'Unknown';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

export const getDocumentTypeLabel = (type: string) => {
  const typeLabels: Record<string, string> = {
    po_pdf: 'Purchase Order',
    material_cert: 'Material Certificate',
    certificate: 'Certificate',
    drawing: 'Drawing',
    specification: 'Specification',
    test_report: 'Test Report',
    inspection_report: 'Inspection Report',
    other: 'Other',
  };
  return typeLabels[type] || type;
};

export const getDocumentTypeColor = (type: string) => {
  const colorMap: Record<string, string> = {
    po_pdf: 'bg-blue-100 text-blue-700',
    material_cert: 'bg-green-100 text-green-700',
    certificate: 'bg-purple-100 text-purple-700',
    drawing: 'bg-orange-100 text-orange-700',
    specification: 'bg-indigo-100 text-indigo-700',
    test_report: 'bg-yellow-100 text-yellow-700',
    inspection_report: 'bg-pink-100 text-pink-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return colorMap[type] || 'bg-gray-100 text-gray-700';
};

export const groupDocumentsByType = (documents: PODocument[]) => {
  const grouped: Record<string, PODocument[]> = {};
  documents.forEach((doc) => {
    if (!grouped[doc.document_type]) {
      grouped[doc.document_type] = [];
    }
    grouped[doc.document_type].push(doc);
  });
  // Sort documents within each group by upload date (newest first)
  Object.keys(grouped).forEach((type) => {
    grouped[type].sort((a, b) => new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime());
  });
  return grouped;
};

export const convertChangelogsToTimeline = (changelogs: POChangeLog[]): TimelineItem[] => {
  return changelogs.map((log) => ({
    id: log.id,
    timestamp: log.change_date,
    user: log.changed_by_details?.full_name || 'Unknown User',
    userRole: undefined,
    action: `Changed ${formatFieldName(log.field_changed)}`,
    field: formatFieldName(log.field_changed),
    oldValue: log.old_value || undefined,
    newValue: log.new_value || undefined,
    reason: log.reason || undefined,
    type: 'update' as const,
  }));
};
